import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PersonalizationStep = ({ data, onUpdate, onNext, onBack }) => {
  const handleNotificationChange = (key, value) => {
    onUpdate('notifications', {
      ...data?.notifications,
      [key]: value
    });
  };

  const handlePrivacyChange = (key, value) => {
    onUpdate('privacy', {
      ...data?.privacy,
      [key]: value
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
          Personalize Your Experience
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Configure your preferences to get the most out of MyBodyToday
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Notification Preferences */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Bell" size={20} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Notification Settings</h3>
          </div>

          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data?.notifications?.dailyReminders}
                onChange={(e) => handleNotificationChange('dailyReminders', e?.target?.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-foreground block">Daily Check-In Reminders</span>
                <span className="text-xs text-muted-foreground">Get reminded to log your symptoms every day</span>
              </div>
            </label>

            {data?.notifications?.dailyReminders && (
              <div className="ml-6 pl-4 border-l-2 border-border">
                <label className="block">
                  <span className="text-sm text-foreground mb-2 block">Reminder Time</span>
                  <Input
                    type="time"
                    value={data?.notifications?.reminderTime}
                    onChange={(e) => handleNotificationChange('reminderTime', e?.target?.value)}
                  />
                </label>
              </div>
            )}

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data?.notifications?.emailNotifications}
                onChange={(e) => handleNotificationChange('emailNotifications', e?.target?.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-foreground block">Email Notifications</span>
                <span className="text-xs text-muted-foreground">Receive important updates via email</span>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data?.notifications?.pushNotifications}
                onChange={(e) => handleNotificationChange('pushNotifications', e?.target?.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-foreground block">Push Notifications</span>
                <span className="text-xs text-muted-foreground">Get instant alerts on your device</span>
              </div>
            </label>
          </div>
        </div>

        {/* Privacy Controls */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Shield" size={20} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Privacy Controls</h3>
          </div>

          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data?.privacy?.shareWithCaregivers}
                onChange={(e) => handlePrivacyChange('shareWithCaregivers', e?.target?.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-foreground block">Share with Caregivers</span>
                <span className="text-xs text-muted-foreground">Allow caregivers to view your health data</span>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data?.privacy?.anonymousAnalytics}
                onChange={(e) => handlePrivacyChange('anonymousAnalytics', e?.target?.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-foreground block">Anonymous Analytics</span>
                <span className="text-xs text-muted-foreground">Help us improve by sharing anonymous usage data</span>
              </div>
            </label>
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-foreground">
                <strong>Your privacy matters.</strong> You can change these settings anytime in your account preferences.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
        <Button
          variant="outline"
          onClick={onBack}
          iconName="ArrowLeft"
          className="sm:flex-1"
        >
          Back
        </Button>
        <Button
          onClick={onNext}
          iconName="ArrowRight"
          iconPosition="right"
          className="sm:flex-1"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default PersonalizationStep;